/**
 * Event dates are UK calendar days (Europe/London), matching the API.
 * Upcoming list: events on today's UK date or later (no grace period after event day).
 * Booking ended state: end of the event day (isPastEventDateEnd).
 */

const UK = "Europe/London";

/** Calendar day from API (YYYY-MM-DD prefix or UTC date from ISO). */
export function parseEventDateAsLocalDay(dateInput: string): Date {
  const trimmed = dateInput.trim();
  const isoDate = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    const [y, m, d] = isoDate.split("-").map(Number);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d);
    }
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  }
  return parsed;
}

/**
 * Local Date with time from API string (uses wall-clock components, no TZ shift).
 * Falls back to midnight when time is absent.
 */
export function parseEventDateTimeLocal(dateInput: string): Date {
  const trimmed = dateInput.trim();
  const m = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const h = Number(m[4] ?? 0);
    const mi = Number(m[5] ?? 0);
    const s = Number(m[6] ?? 0);
    return new Date(y, mo - 1, d, h, mi, s);
  }
  return parseEventDateAsLocalDay(trimmed);
}

/** True when the API value includes a non-midnight time. */
export function eventHasTimeOfDay(dateInput: string): boolean {
  const m = dateInput.trim().match(/[T ](\d{2}):(\d{2})/);
  if (!m) return false;
  return !(m[1] === "00" && m[2] === "00");
}

/** UK timezone short name for the event calendar day (BST or GMT). */
export function ukTimezoneAbbreviation(dateInput: string): "BST" | "GMT" {
  const day = dateInput.trim().slice(0, 10);
  const probe = /^\d{4}-\d{2}-\d{2}$/.test(day)
    ? new Date(`${day}T12:00:00Z`)
    : parseEventDateTimeLocal(dateInput);
  const tz =
    new Intl.DateTimeFormat("en-GB", {
      timeZone: UK,
      timeZoneName: "short",
    })
      .formatToParts(probe)
      .find((p) => p.type === "timeZoneName")?.value ?? "GMT";
  return /BST|Summer/i.test(tz) ? "BST" : "GMT";
}

/** Date (+ time with UK tz) label for event cards and details. */
export function formatEventDateTimeLabel(
  dateInput: string,
  locale?: string,
): string {
  if (!dateInput?.trim()) return "";
  const dateLocale = locale || undefined;
  if (!eventHasTimeOfDay(dateInput)) {
    return parseEventDateAsLocalDay(dateInput).toLocaleDateString(dateLocale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  const formatted = parseEventDateTimeLocal(dateInput).toLocaleString(
    dateLocale,
    {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
  );
  return `${formatted} (${ukTimezoneAbbreviation(dateInput)})`;
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const n = new Date(Date.UTC(y, m - 1, d + days));
  return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}-${String(n.getUTCDate()).padStart(2, "0")}`;
}

function ukYmd(ts: number): string {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: UK,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ts));
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "0";
  return `${g("year")}-${g("month")}-${g("day")}`;
}

function startOfUkDay(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  let lo = Date.UTC(y, m - 1, d) - 36e6;
  let hi = Date.UTC(y, m - 1, d) + 36e6;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (ukYmd(mid) < iso) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** End of event day: 23:59:59.999 UK (same as next UK midnight minus 1ms). */
export function getEndOfEventDayLocal(dateInput: string): Date {
  const iso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return new Date(NaN);
  }
  return new Date(startOfUkDay(addDaysIso(iso, 1)) - 1);
}

export function isPastEventDateEnd(dateInput: string, now: Date = new Date()): boolean {
  return now > getEndOfEventDayLocal(dateInput);
}

/** True when the event falls on today's UK calendar day. */
export function isEventTodayInUk(dateInput: string, now: Date = new Date()): boolean {
  const eventIso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventIso)) return false;
  return eventIso === ukYmd(now.getTime());
}

export type EventCardPhase = "expired" | "today" | "upcoming";

export function getEventCardPhase(
  dateInput: string,
  now: Date = new Date(),
): EventCardPhase {
  if (isPastEventDateEnd(dateInput, now)) return "expired";
  if (isEventTodayInUk(dateInput, now)) return "today";
  return "upcoming";
}

/** True when the event belongs on the upcoming list (today or future UK calendar day). */
export function isEventDateStillActive(dateInput: string, now: Date = new Date()): boolean {
  const eventIso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventIso)) {
    return !isPastEventDateEnd(dateInput, now);
  }
  return eventIso >= ukYmd(now.getTime());
}

/** True when the event day has ended (for past / attended lists). */
export function isEventPastForAttendedList(
  dateInput: string,
  now: Date = new Date(),
): boolean {
  return isPastEventDateEnd(dateInput, now);
}

/**
 * True when now is within 48 hours before the UK start of the event day,
 * through the end of that event day. Used to reveal attendees on event cards
 * even if the viewer has not booked.
 */
export function isWithin48HoursBeforeEvent(
  dateInput: string,
  now: Date = new Date(),
): boolean {
  const eventIso = dateInput.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eventIso)) return false;

  const eventStartMs = startOfUkDay(eventIso);
  const windowStartMs = eventStartMs - 48 * 60 * 60 * 1000;
  const eventEndMs = startOfUkDay(addDaysIso(eventIso, 1)) - 1;
  const nowMs = now.getTime();

  return nowMs >= windowStartMs && nowMs <= eventEndMs;
}
